/**
 * Le rythme d'une fenêtre d'accord : qui frappe, quand, et jusqu'où.
 *
 * Pur : ni React, ni transport, ni horloge. Le dessin est dans
 * `score-layout.ts` et la validation dans `use-rhythm-take` ; ce qui est ici
 * est la lecture des notes du niveau, où elle se teste. Même partage que
 * `beats.ts` et `take.ts`.
 *
 * ## Pourquoi la barre du temps ne suffisait plus
 *
 * Elle disait *combien de temps il reste*, gradué, et c'était juste tant qu'un
 * accord se plaquait d'un bloc et se tenait. Le niveau trois donne à chaque
 * main son propre rythme : la gauche sur un et trois, la droite sur les quatre
 * temps, et une barre qui se vide n'a aucun moyen de le montrer. Elle mesurait
 * la fenêtre ; ce qu'il faut lire maintenant, c'est **ce qu'on joue dedans**.
 *
 * ## Deux lectures de la même chose, et il en faut deux
 *
 * Les **portes** sont l'union des instants : c'est ce que le joueur doit
 * franchir, et c'est ce que la tête de lecture parcourt. Les **segments** sont
 * par main : une frappe de la gauche dure jusqu'à la frappe suivante *de la
 * gauche*, et pas jusqu'à la prochaine porte, sinon une basse posée sous un
 * accord qui répond à contretemps se couperait à l'attaque de l'autre main et
 * l'écran écrirait un staccato que le morceau n'a pas. C'est la même règle que
 * `pulseStrikes` côté Go, et elle est ici pour la même raison.
 *
 * ## Tout est en ticks, et rien n'est en fraction de fenêtre
 *
 * Les positions ont d'abord été des fractions, entre 0 et 1, parce que le rail
 * dessinait **une** fenêtre de bord à bord et que la fraction s'y traduisait
 * directement en pourcentage. Depuis que le rail est une frise continue, la
 * fenêtre n'est plus l'unité de dessin : elle est un morceau d'un ruban qui va
 * du début à la fin du morceau, et deux fenêtres voisines ne peuvent se
 * raccorder qu'en ticks. La géométrie — combien de pixels vaut un tick — reste
 * au dessin, qui est le seul à connaître la place dont il dispose.
 */

/** Une main, telle que la fenêtre la nomme. */
export type RhythmHand = "left" | "right";

/** Une note frappée, réduite à ce que le rythme en lit. */
export type RhythmNote = {
  readonly midi: number;
  readonly startTick: number;
};

/**
 * Un instant où le joueur a quelque chose à faire.
 *
 * Les deux mains sont dans la même porte quand elles frappent ensemble, et
 * c'est le cas de la première : elle énonce l'accord. Une porte à une seule
 * main n'attend que celle-là ; l'autre garde ses touches enfoncées, ce qui
 * n'est pas une frappe et n'a rien à valider.
 */
export type RhythmGate = {
  /** Le tick de la frappe, dans le repère du morceau. */
  readonly tick: number;
  /** Les touches que la gauche enfonce ici. Vide quand elle ne joue pas. */
  readonly left: readonly number[];
  readonly right: readonly number[];
};

/** Ce qu'une main frappe à un instant, et jusqu'où ça tient. */
export type RhythmSegment = {
  /** Le tick de la frappe, dans le repère du morceau. */
  readonly fromTick: number;
  /** La frappe suivante *de cette main*, ou la fin de la fenêtre. */
  readonly toTick: number;
  /**
   * Les touches enfoncées ici par cette main, de la plus grave à la plus aiguë.
   *
   * Toutes, et non la plus grave : une main droite plaque une triade, et une
   * portée les écrit toutes les trois. C'est ce qui remplace la hauteur
   * normalisée que ce type portait du temps des deux voies — une valeur entre
   * zéro et un, relative à l'ambitus de la **fenêtre**, donc incomparable d'une
   * fenêtre à l'autre et muette sur l'octave. Voir `staff.ts`.
   */
  readonly keys: readonly number[];
};

/** Une graduation de la règle, et le numéro de mesure quand c'en est une. */
export type RhythmMark = {
  /** Le tick du temps, dans le repère du morceau. */
  readonly tick: number;
  readonly bar: boolean;
  /** Le numéro de mesure du morceau, ou `null` sur un simple temps. */
  readonly measure: number | null;
};

export type WindowRhythm = {
  readonly gates: readonly RhythmGate[];
  readonly left: readonly RhythmSegment[];
  readonly right: readonly RhythmSegment[];
};

const EMPTY: WindowRhythm = { gates: [], left: [], right: [] };

/**
 * Deux frappes plus rapprochées que ça sont la même.
 *
 * Un huitième de temps. Aux niveaux fabriqués les deux mains partagent le tick
 * exact, donc la tolérance ne sert à rien ; à partir de la couture ce sont les
 * notes du morceau, où un accord roulé ou un léger décalage expressif étale la
 * même frappe sur quelques ticks. Deux portes à cet écart-là ne se jouent pas,
 * elles se subissent.
 */
function fusionOf(beatTicks: number): number {
  return Math.max(1, Math.round(beatTicks / 8));
}

/**
 * Le rythme d'une fenêtre, tel que l'écran le montre et le valide.
 *
 * Les notes attendues sont celles **frappées dans la fenêtre** : celles qui
 * sonnent depuis avant sont tenues, et une note tenue n'est pas un geste.
 */
export function windowRhythm({
  startTick,
  endTick,
  left,
  right,
  beatTicks,
}: {
  readonly startTick: number;
  readonly endTick: number;
  readonly left: readonly RhythmNote[];
  readonly right: readonly RhythmNote[];
  /** La durée d'un temps, en ticks : elle donne la tolérance de fusion. */
  readonly beatTicks: number;
}): WindowRhythm {
  const span = endTick - startTick;

  if (span <= 0) {
    return EMPTY;
  }

  const fusion = fusionOf(beatTicks);
  const ticks = fuse(
    [...left, ...right]
      .map((note) => note.startTick)
      .filter((tick) => tick >= startTick && tick < endTick),
    fusion,
  );

  const gates = ticks.map((tick) => ({
    tick,
    left: keysAt(left, tick, fusion),
    right: keysAt(right, tick, fusion),
  }));

  return {
    gates,
    left: laneOf(left, startTick, endTick, fusion),
    right: laneOf(right, startTick, endTick, fusion),
  };
}

/**
 * Les instants distincts, dans l'ordre, deux frappes voisines n'en faisant
 * qu'une.
 *
 * Le tick retenu est le **plus petit** de l'amas, qui est celui qu'on entend :
 * un accord roulé commence quand sa première note sonne, pas quand la dernière
 * arrive.
 */
function fuse(ticks: readonly number[], fusion: number): number[] {
  const out: number[] = [];

  for (const tick of [...ticks].sort((a, b) => a - b)) {
    const last = out[out.length - 1];

    if (last === undefined || tick - last > fusion) {
      out.push(tick);
    }
  }

  return out;
}

/** Les touches d'une main à un instant, à la tolérance de fusion près. */
function keysAt(notes: readonly RhythmNote[], tick: number, fusion: number): number[] {
  return [
    ...new Set(
      notes
        .filter((note) => note.startTick >= tick && note.startTick - tick <= fusion)
        .map((note) => note.midi),
    ),
  ].sort((a, b) => a - b);
}

/**
 * La voie d'une main : une frappe, ses touches, et jusqu'où elle tient.
 *
 * ## Pourquoi la voie porte des touches depuis le niveau de la basse
 *
 * Aux niveaux fabriqués la main gauche joue une note par fenêtre, donc « quand »
 * dit tout. À la basse publiée elle en joue trois en médiane et neuf au neuvième
 * décile, et elle change de hauteur à l'intérieur de la fenêtre sur 63 % d'entre
 * elles : « quand » ne dit plus quelle note. C'est le seul objet de l'écran qui
 * le dise, et c'est pour cela qu'il porte une portée plutôt que deux voies : le
 * milieu de l'écran, lui, garde les grands noms d'accord.
 *
 * ## Toutes les touches, et non la plus grave
 *
 * La voie n'en gardait qu'une, la plus grave, parce qu'un trait dans une bande
 * ne peut être qu'à une hauteur à la fois. Une portée n'a pas cette limite :
 * elle écrit les trois notes d'une triade là où la voie en écrivait une, et
 * c'est ce qui rend le renversement visible au lieu d'être seulement nommé.
 *
 * ## Une frappe tient jusqu'à la frappe suivante *de sa main*
 *
 * Et non jusqu'à la prochaine porte : une basse posée sous un accord qui répond
 * à contretemps se couperait à l'attaque de l'autre main, et l'écran écrirait
 * un staccato que le morceau n'a pas. C'est aussi ce qui fait qu'une note tenue
 * plus longtemps que sa voisine ne se dessine pas plus longue : on grave le
 * geste de la main, pas l'enveloppe de chaque touche.
 */
function laneOf(
  notes: readonly RhythmNote[],
  startTick: number,
  endTick: number,
  fusion: number,
): RhythmSegment[] {
  const ticks = fuse(
    notes.map((note) => note.startTick).filter((tick) => tick >= startTick && tick < endTick),
    fusion,
  );

  return ticks.map((tick, at) => ({
    fromTick: tick,
    toTick: ticks[at + 1] ?? endTick,
    keys: keysAt(notes, tick, fusion),
  }));
}

/**
 * La règle : les temps d'une étendue, et le numéro des mesures.
 *
 * La borne de gauche est **incluse** quand elle tombe sur un temps, à la
 * différence de `beats.ts` où les deux étaient exclues : la barre s'y
 * arrêtait, la règle y commence, et un « 1 » posé au bord est ce qui rattache
 * le passage au morceau qu'on lit ailleurs.
 *
 * Comptés depuis le début du morceau et non depuis l'étendue demandée : un
 * passage qui commence à contretemps garde alors ses temps là où le morceau
 * les a, ce qui est le seul endroit où ils veulent dire quelque chose.
 *
 * Tous les temps, sans plafond. Le plafond d'avant — au-delà de douze
 * graduations, seules les mesures — servait un rail qui étirait **une**
 * fenêtre sur toute sa largeur : une fenêtre tenue sur seize mesures y faisait
 * une trame et non une échelle. Sur une frise à zoom constant la densité ne
 * dépend plus de la durée de la fenêtre mais du seul tempo, et un temps y
 * mesure toujours la même chose.
 */
export function beatMarks({
  fromTick,
  toTick,
  beatTicks,
  beatsPerBar,
}: {
  readonly fromTick: number;
  readonly toTick: number;
  /** La durée d'un temps, en ticks. */
  readonly beatTicks: number;
  readonly beatsPerBar: number;
}): RhythmMark[] {
  if (beatTicks <= 0 || beatsPerBar <= 0) {
    return [];
  }

  const barTicks = beatTicks * beatsPerBar;
  const out: RhythmMark[] = [];

  for (
    let tick = Math.ceil(fromTick / beatTicks) * beatTicks;
    tick < toTick;
    tick += beatTicks
  ) {
    const bar = tick % barTicks === 0;

    out.push({
      tick,
      bar,
      // Les mesures sont numérotées à partir de un, comme l'analyseur les
      // publie et comme la structure du studio les affiche.
      measure: bar ? Math.floor(tick / barTicks) + 1 : null,
    });
  }

  return out;
}

/**
 * Les touches qu'une porte demande, les deux mains réunies.
 *
 * Réunies parce que le joueur n'a qu'une paire de mains et que la porte est un
 * instant : ce qui est demandé, c'est *ce qui sonne ici*. La distinction des
 * mains sert au dessin, où elle dit laquelle bouge ; elle ne sert pas à la
 * validation, qui ne regarde que des touches.
 */
export function gateKeys(gate: RhythmGate): readonly number[] {
  return [...new Set([...gate.left, ...gate.right])].sort((a, b) => a - b);
}

/**
 * La porte est-elle franchie ?
 *
 * Deux conditions, et il faut les deux.
 *
 * **Chacune de ses touches a été refrappée depuis qu'elle s'est ouverte.** Pas
 * « est enfoncée » : une main qui tient encore l'accord précédent enfonce déjà
 * les bonnes touches sans avoir rien joué, et la porte s'ouvrirait toute seule
 * à l'instant même où elle apparaît. C'est le contraire de ce qu'on demande —
 * refrapper — donc c'est le compte des frappes qui décide, et non leur état.
 *
 * **Et elles sonnent ensemble.** Le compte seul validait l'accord joué note
 * après note, aussi lentement qu'on voulait : quatre frappes séparées d'une
 * seconde faisaient une porte, alors qu'elles ne font pas un accord. L'ensemble
 * est celui de `useKeyStrikes`, qui garde une touche un court instant après sa
 * frappe : un accord roulé en est un, un arpège n'en est pas un.
 *
 * Corollaire voulu : une main qui ne figure pas dans la porte n'a rien à faire,
 * et elle peut tenir, relâcher ou préparer la suite sans que rien ne l'attende.
 */
export function gateCrossed(
  gate: RhythmGate,
  /** Combien de fois chaque touche a été enfoncée, maintenant. */
  presses: ReadonlyMap<number, number>,
  /** Le même compte au moment où la porte s'est ouverte. */
  baseline: ReadonlyMap<number, number>,
  /** Les touches qui sonnent ensemble à cet instant. */
  together: ReadonlySet<number>,
): boolean {
  const keys = gateKeys(gate);

  return (
    keys.length > 0 &&
    keys.every((midi) => (presses.get(midi) ?? 0) > (baseline.get(midi) ?? 0)) &&
    keys.every((midi) => together.has(midi))
  );
}
